<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { HtmlToMarkdownConverter } from '../../assistant/settingsPage/HtmlToMarkdownConverter.js';
import { MarkdownToHtmlConverter } from '../../assistant/settingsPage/MarkdownToHtmlConverter.js';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: 1000,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const editor = ref(null);

const characterCount = computed(() => {
  return props.modelValue ? props.modelValue.length : 0;
});

const initializeEditor = () => {
  const initialHtml = props.modelValue
    ? MarkdownToHtmlConverter.convertToHtml(props.modelValue)
    : '';

  editor.value = new Editor({
    extensions: [StarterKit],
    content: initialHtml,
    editorProps: {
      attributes: {
        placeholder: props.placeholder,
      },
    },
    // eslint-disable-next-line no-shadow
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdown = new HtmlToMarkdownConverter().convertToMarkdown(html);

      // Only emit if the markdown content has actually changed
      if (markdown !== props.modelValue) {
        emit('update:modelValue', markdown);
      }
    },
    onBlur: () => {
      emit('blur');
    },
  });
};

// Watch for external changes to modelValue (e.g., form reset)
watch(
  () => props.modelValue,
  newValue => {
    if (editor.value && !editor.value.isFocused) {
      const currentHtml = editor.value.getHTML();
      const expectedHtml = newValue
        ? MarkdownToHtmlConverter.convertToHtml(newValue)
        : '';

      // Only update editor if content is actually different
      if (currentHtml !== expectedHtml) {
        editor.value.commands.setContent(expectedHtml);
      }
    }
  }
);

onMounted(() => {
  initializeEditor();
});

onUnmounted(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});
</script>

<template>
  <div class="mb-4">
    <label
      class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
    >
      {{ label }}
    </label>
    <div
      class="border border-slate-300 dark:border-slate-600 rounded-lg focus-within:border-blue-500 dark:focus-within:border-blue-400 transition-colors"
      :class="{ 'border-red-500 dark:border-red-400': hasError }"
    >
      <EditorContent :editor="editor" />
    </div>
    <div class="flex justify-between items-center mt-2">
      <p
        v-if="hasError && errorMessage"
        class="text-sm text-red-500 dark:text-red-400"
      >
        {{ errorMessage }}
      </p>
      <div class="text-xs text-slate-500 dark:text-slate-400 ml-auto">
        {{ `${characterCount} / ${maxLength}` }}
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.ProseMirror) {
  padding: 12px;
  min-height: 120px;
  outline: none;
  font-size: 14px;
  line-height: 1.5;
  color: #334155; /* slate-700 */
}

:deep(.dark .ProseMirror) {
  color: #cbd5e1; /* slate-300 */
}

:deep(.ProseMirror p) {
  margin: 0 0 8px 0;
}

:deep(.ProseMirror ul),
:deep(.ProseMirror ol) {
  padding-left: 20px;
  margin: 8px 0;
}

:deep(.ProseMirror li) {
  margin: 4px 0;
}

:deep(.ProseMirror strong) {
  font-weight: 600;
}

:deep(.ProseMirror em) {
  font-style: italic;
}

:deep(.ProseMirror code) {
  background-color: #f1f5f9; /* slate-100 */
  color: #1e293b; /* slate-800 */
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 0.875em;
}

:deep(.dark .ProseMirror code) {
  background-color: #1e293b; /* slate-800 */
  color: #e2e8f0; /* slate-200 */
}

:deep(.ProseMirror pre) {
  background-color: #f1f5f9; /* slate-100 */
  color: #1e293b; /* slate-800 */
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
}

:deep(.dark .ProseMirror pre) {
  background-color: #1e293b; /* slate-800 */
  color: #e2e8f0; /* slate-200 */
}

:deep(.ProseMirror blockquote) {
  border-left: 4px solid #cbd5e1; /* slate-300 */
  padding-left: 16px;
  margin: 8px 0;
  font-style: italic;
}

:deep(.dark .ProseMirror blockquote) {
  border-left-color: #475569; /* slate-600 */
}

:deep(.ProseMirror h1),
:deep(.ProseMirror h2),
:deep(.ProseMirror h3),
:deep(.ProseMirror h4),
:deep(.ProseMirror h5),
:deep(.ProseMirror h6) {
  font-weight: 600;
  margin: 16px 0 8px 0;
  line-height: 1.3;
}

:deep(.ProseMirror h1) {
  font-size: 1.5em;
}

:deep(.ProseMirror h2) {
  font-size: 1.3em;
}

:deep(.ProseMirror h3) {
  font-size: 1.2em;
}

:deep(.ProseMirror h4) {
  font-size: 1.1em;
}

:deep(.ProseMirror h5) {
  font-size: 1em;
}

:deep(.ProseMirror h6) {
  font-size: 0.9em;
}

:deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: #94a3b8; /* slate-400 */
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

:deep(.dark .ProseMirror p.is-editor-empty:first-child::before) {
  color: #64748b; /* slate-500 */
}
</style>
